import dayjs from "dayjs";
import { FareDetails, TripDetails, TripSlice, FareSliceDetails } from "redmond";

export const getSliceIndex = (
  departure: boolean,
  details: TripDetails | FareDetails
): number => {
  return details.slices.findIndex((slice: TripSlice | FareSliceDetails) =>
    departure ? slice.outgoing : !slice.outgoing
  );
};

export const ELIGIBLE_CORPORATE_RATE_CABIN_CLASSES = [
  "E", "K", "H", "Q", "B", "V", "W", "M", "Z", "O", "U", "P", "J", "C", "D", "I",
] as const;
export const JETBLUE_CARRIER_CODE = "B6";

const getIsCorporateRateFareSlice = (
  fareSlice: FareSliceDetails | undefined
): boolean => {
  const fareSegments = fareSlice?.fareDetails?.segments ?? [];
  if (fareSegments.length === 0) return false;
  const eligibleCabinClasses: readonly string[] =
    ELIGIBLE_CORPORATE_RATE_CABIN_CLASSES;
  return fareSegments.every(
    (s) =>
      s.carrierCabinClass != null &&
      eligibleCabinClasses.includes(s.carrierCabinClass)
  );
};

const getIsCorporateRateTripSlice = (tripSlice: TripSlice | undefined): boolean => {
  const tripSegments = tripSlice?.segmentDetails ?? [];
  if (tripSegments.length === 0) return false;
  return tripSegments.every(
    (s) =>
      s.marketingAirline?.code === JETBLUE_CARRIER_CODE &&
      s.operatingAirline?.code === JETBLUE_CARRIER_CODE
  );
};

export const getIsCorporateRateSlice = (
  departure: boolean,
  tripDetails: TripDetails | undefined,
  fareDetails: FareDetails | undefined
): boolean => {
  if (!tripDetails || !fareDetails) return false;

  const fareSliceIndex = getSliceIndex(departure, fareDetails);
  const tripSliceIndex = getSliceIndex(departure, tripDetails);
  if (fareSliceIndex === -1 || tripSliceIndex === -1) return false;

  return (
    getIsCorporateRateFareSlice(fareDetails.slices[fareSliceIndex]) &&
    getIsCorporateRateTripSlice(tripDetails.slices[tripSliceIndex])
  );
};

export const getIsCorporateRateBooking = (
  tripDetails: TripDetails | null | undefined,
  fareDetails: FareDetails | undefined
): boolean => {
  if (!tripDetails || !fareDetails) return false;
  if (
    tripDetails.slices.length === 0 ||
    tripDetails.slices.length !== fareDetails.slices.length
  ) {
    return false;
  }

  return tripDetails.slices.every(
    (tripSlice, idx) =>
      getIsCorporateRateFareSlice(fareDetails.slices[idx]) &&
      getIsCorporateRateTripSlice(tripSlice)
  );
};

export const getMulticityParamsFromTripDetails = (tripDetails: TripDetails) => {
  return tripDetails.slices.reduce((acc, slice, idx) => {
    const { originCode, destinationCode, departureTime } = slice;
    acc[`origin${idx}`] = originCode;
    acc[`destination${idx}`] = destinationCode;
    acc[`departureDate${idx}`] = dayjs(departureTime).format("YYYY-MM-DD");
    return acc;
  }, {});
};
